<script setup lang="ts">
import { ref } from 'vue';
import { CalendlyEvent } from '@/consent/medicare/domain/CalendlyEventResponse';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import ScheduleAppointment from '@/shared/infrastructure/components/ScheduleAppointment.vue';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { ToastTypes } from '@hfc/ui-core';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

const { getSmallBusinessConsultationLink } = useUrlHelper();

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();
const { appStore } = useAppStore();

const getFirstString = (str: string): string => {
  const firstString = str?.split(' ')[0] ?? '';

  return firstString;
};

const prefill = ref({
  customAnswers: {
    a1: `+1${smallBusinessState.value.application.company.primary_contact.phone}`,
    a3: smallBusinessState.value.application.company.name ?? '',
    a4: smallBusinessState.value.application.company.main_address.zip_code ?? '',
    a5: smallBusinessState.value.companySize >= 10 ? '10+' : smallBusinessState.value.companySize,
  },
  firstName: getFirstString(smallBusinessState.value.application.company.primary_contact.first_name),
  lastName: getFirstString(smallBusinessState.value.application.company.primary_contact.last_name),
  email: smallBusinessState.value.application.company.primary_contact.email
});

const loading = ref(false);
const appointmentLink = getSmallBusinessConsultationLink();

interface emitInterface {
  (event: 'hideNavigation'): void
  (event: 'setLoading', value: boolean): void
}

const emits = defineEmits<emitInterface>();

const saveAppointment = async (data: CalendlyEvent):Promise<void> => {
  await smallBusinessStore.updateApplication({ appointment: data });
  const response = await smallBusinessStore.saveApplication();

  if (response && response.error) {
    appStore.addToast({ message: response.error.message ?? 'Error sending the application. Please try again.', type: ToastTypes.DANGER });
  }
};

const handleDateAndTimeSelected = async (data: CalendlyEvent):Promise<void> => {
  emits('setLoading', true);
  await saveAppointment(data);
  emits('setLoading', false);
};

const handleEventScheduled = async (data: CalendlyEvent):Promise<void> => {
  loading.value = true;
  await saveAppointment(data);
  loading.value = false;
  emits('hideNavigation');
};

defineExpose({
  sectionSubmit: () => true
});

</script>
<template>
  <BaseSection title="">
    <ScheduleAppointment :appointment-link="appointmentLink" :loading="loading" @date-and-time-selected="handleDateAndTimeSelected" @event-scheduled="handleEventScheduled" :prefill="prefill" />
  </BaseSection>
</template>
